import React from 'react';
import {
    DetailDossierTitle,
    DetailDossierToolbar,
    DossierActionContext,
} from '@europrocurement/l2d-modules';
import {
    selectedOffer,
    selectedPrescripteursSelector,
    selectedSousClientSelector,
    TiersAppDispatch,
    useOffreService,
    useTiersService,
} from '@europrocurement/l2d-domain';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentDossierCtx } from '@europrocurement/l2d-modules/modules/Dossiers/providers/CurrentDossierProvider';

const DetailDossierHeader: React.FunctionComponent = function () {
    const { dossier, siblings } = useCurrentDossierCtx();
    const { selectSousClients, selectPrescripteur } = useTiersService();
    const { selectOffer } = useOffreService();
    const { dossiersUpdated, setDossiersUpdated } = React.useContext(DossierActionContext);
    const dispatch = useDispatch<TiersAppDispatch>();

    React.useEffect(() => {
        if (!dossier) return;
        if (dossiersUpdated.length) {
            setDossiersUpdated([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossiersUpdated, dossier]);

    const prescripteurIdFromCurrentDossier = dossier?.prescripteurId;
    const offreIdFromCurrentDossier = dossier?.offreId;
    const clientOpereIdFromCurrentDossier = dossier?.clientOpereId;

    const prescripteur = useSelector(selectedPrescripteursSelector);
    const offre = useSelector(selectedOffer);
    const clientOpere = useSelector(selectedSousClientSelector);

    React.useEffect(() => {
        if (!prescripteurIdFromCurrentDossier) return;

        dispatch(selectPrescripteur({ id: prescripteurIdFromCurrentDossier?.toString() }));
    }, [dispatch, prescripteurIdFromCurrentDossier, selectPrescripteur]);

    React.useEffect(() => {
        if (!offreIdFromCurrentDossier) return;

        dispatch(selectOffer({ id: offreIdFromCurrentDossier }));
    }, [dispatch, offreIdFromCurrentDossier, selectOffer]);

    React.useEffect(() => {
        if (!clientOpereIdFromCurrentDossier) return;

        dispatch(selectSousClients({ id: clientOpereIdFromCurrentDossier }));
    }, [dispatch, clientOpereIdFromCurrentDossier, selectSousClients]);

    // URLs et données formatées
    const clientOpereSocieteDotComUrl = React.useMemo(() => {
        if (!clientOpere?.siren) return undefined;
        return `https://www.societe.com/cgi-bin/fiche?rncs=${clientOpere.siren}`;
    }, [clientOpere?.siren]);

    /** Données du dossier formatées pour l'affichage */
    const formattedDossier = React.useMemo(
        () => ({
            statut: dossier?.statutLibelle as string,
            statutId: dossier?.statutId as number,
            societeRef: dossier?.societeRef as string,
            id: dossier?.id as number,
        }),
        [dossier?.statutLibelle, dossier?.societeRef, dossier?.statutId, dossier?.id],
    );

    const formattedPrescripteur: {
        id: number;
        raisonSociale: string;
        codeComptable: string;
        urlToDetails?: string;
    } = {
        id: prescripteur?.id as number,
        raisonSociale: (prescripteur?.nomDentreprise as string) || 'Pas de raison sociale',
        codeComptable: prescripteur?.relationsSocietes[0].codeClient || 'Pas de code comptable',
        urlToDetails: undefined,
    };

    const formattedOffre: { libelle: string } = {
        libelle: offre?.libelle || 'Aucune offre',
    };

    const formattedClientOpere = React.useMemo(
        () => ({
            raisonSociale: clientOpere?.raisonSociale || 'Pas de raison sociale',
            siren: clientOpere?.siren,
            urlToSocieteDotCom: clientOpereSocieteDotComUrl,
            id: dossier?.clientOpereId as number,
        }),
        [
            clientOpere?.raisonSociale,
            clientOpere?.siren,
            clientOpereSocieteDotComUrl,
            dossier?.clientOpereId,
        ],
    );

    const isMultiDossiers = siblings && siblings.length > 1;

    if (!dossier) {
        console.log('Dossier non trouvé');
        return null;
    }

    return (
        <>
            <DetailDossierToolbar
                dossier={dossier}
                source="FRONT"
                siblingDossiers={siblings}
                isMultiDossiers={isMultiDossiers}
            />
            <DetailDossierTitle
                dossier={formattedDossier}
                prescripteur={formattedPrescripteur}
                offre={formattedOffre}
                clientOpere={formattedClientOpere}
                color="primary"
                icon={isMultiDossiers ? 'folders' : 'folder'}
            />
        </>
    );
};

export default DetailDossierHeader;
